package com.videoanalyzer.web.components

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.models.UserProfile
import com.videoanalyzer.web.state.Screen
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * App header with navigation and user info.
 *
 * ANDROID EQUIVALENT: TopAppBar with navigation
 */
@Composable
fun Header(
    user: UserProfile?,
    currentScreen: Screen,
    analysesRemaining: Int,
    onNavigate: (Screen) -> Unit,
    onSignOut: () -> Unit
) {
    Header({
        classes("fade-in-down", "nav-glass")
        style {
            padding(12.px, 16.px)
            position(Position.Sticky)
            top(0.px)
            property("z-index", "100")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                maxWidth(1200.px)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
            }
        }) {
            // Logo & Brand
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    cursor("pointer")
                    property("transition", "transform 0.2s ease")
                }
                onClick { onNavigate(Screen.Home) }
            }) {
                Logo(size = LogoSize.SMALL)
                LogoText(size = LogoSize.MEDIUM)
            }

            // Navigation & User Section
            if (user != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(20.px)
                    }
                }) {
                    // Navigation Links
                    NavLinks(currentScreen, onNavigate)

                    // Usage indicator
                    UsageBadge(analysesRemaining, user.subscriptionTier, user.creditBalance)

                    // User menu
                    UserMenu(user, onNavigate, onSignOut)
                }
            }
        }
    }
}

@Composable
private fun NavLinks(currentScreen: Screen, onNavigate: (Screen) -> Unit) {
    Nav({
        style {
            display(DisplayStyle.Flex)
            gap(2.px)
            backgroundColor(Color("rgba(39, 39, 42, 0.6)"))
            padding(3.px)
            borderRadius(8.px)
            property("backdrop-filter", "blur(8px)")
        }
    }) {
        NavLink("Analyze", Screen.Home, currentScreen, onNavigate)
        NavLink("History", Screen.History, currentScreen, onNavigate)
        NavLink("Pricing", Screen.Pricing, currentScreen, onNavigate)
        NavLink("Contact", Screen.Contact, currentScreen, onNavigate)
    }
}

@Composable
private fun NavLink(
    label: String,
    target: Screen,
    current: Screen,
    onClick: (Screen) -> Unit
) {
    val isActive = when {
        target is Screen.Home && current is Screen.Home -> true
        target is Screen.History && current is Screen.History -> true
        target is Screen.Pricing && current is Screen.Pricing -> true
        target is Screen.Contact && current is Screen.Contact -> true
        else -> false
    }

    Button({
        style {
            backgroundColor(if (isActive) Color("#3F3F46") else Color.transparent)
            color(if (isActive) Color("#FAFAFA") else Color("#A1A1AA"))
            border(0.px)
            padding(8.px, 14.px)
            borderRadius(4.px)
            cursor("pointer")
            fontWeight(if (isActive) 500 else 400)
            fontSize(13.px)
            property("transition", "background-color 0.15s ease")
        }
        onClick { onClick(target) }
    }) {
        Text(label)
    }
}

@Composable
private fun UsageBadge(remaining: Int, tier: String, creditBalance: Int) {
    val isPro = tier == "pro"
    val hasCredits = creditBalance > 0
    val isCritical = !isPro && !hasCredits && remaining <= 1

    Div({
        if (isCritical) {
            classes("usage-critical")
        }
        style {
            padding(4.px, 10.px)
            borderRadius(4.px)
            fontSize(11.px)
            fontWeight(600)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(4.px)
            if (isPro) {
                // PRO badge - subtle white/gray
                backgroundColor(Color("rgba(255, 255, 255, 0.1)"))
                color(Color("#FAFAFA"))
            } else if (remaining > 0 || hasCredits) {
                // Normal state - subtle gray
                backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                color(Color("#9CA3AF"))
            } else if (!isCritical) {
                // No remaining - red warning
                backgroundColor(Color("rgba(255, 0, 0, 0.15)"))
                color(Color("#FF0000"))
            }
        }
    }) {
        when {
            isPro -> {
                Text("PRO")
            }
            hasCredits -> {
                Text("$creditBalance credits")
            }
            else -> {
                Text("$remaining left")
            }
        }
    }
}

@Composable
private fun UserMenu(
    user: UserProfile,
    onNavigate: (Screen) -> Unit,
    onSignOut: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
        }
    }) {
        // User avatar with email initial - neutral gray
        Div({
            style {
                width(32.px)
                height(32.px)
                borderRadius(8.px)
                backgroundColor(Color("#3F3F46"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                color(Color("#FAFAFA"))
                fontWeight(600)
                fontSize(13.px)
                cursor("pointer")
                property("transition", "transform 0.2s ease, box-shadow 0.2s ease")
            }
            onClick { onNavigate(Screen.Settings) }
        }) {
            Text(user.email.firstOrNull()?.uppercase() ?: "U")
        }

        // Settings button
        Button({
            style {
                backgroundColor(Color("#27272A"))
                color(Color("#A1A1AA"))
                border(0.px)
                padding(8.px)
                borderRadius(6.px)
                cursor("pointer")
                fontSize(14.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
            onClick { onNavigate(Screen.Settings) }
        }) {
            Text("⚙️")
        }

        // Sign out button
        Button({
            classes("btn", "btn-secondary")
            style {
                padding(8.px, 12.px)
                fontSize(12.px)
            }
            onClick { onSignOut() }
        }) {
            Text("Sign Out")
        }
    }
}
